import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { PillView, useAction } from "../../../components/ui";
import { date } from "../../../lib/format";
import { revokeShare, setIgEnabled, unlockShare } from "./data";
import { useBase, useEntitlements, useIgOverview } from "./hooks";
import { blockLabel, consentInfo, formatCount, igStatusPill, nameOf, pill, shareState, shareViewStats } from "./model";
import { DemoNote } from "./parts";
import { TarifHinweis, insightsVorbereitungText, useInsightsBereit } from "../../../components/Tarif";
import { ShareTable } from "./InsightsFreigeben";
import { t, ta } from "../../../lib/sprache";

/** Insights-Überblick (Reiter „Insights“ des Altportals, AD-193/AD-194/AD-199): Konten, Einwilligungen, alle Freigaben. */
export function InsightsUebersicht() {
  const ws = useStaffWorkspace();
  const tz = ws.org.timezone;
  const { base, error, loading, noAccess, retry } = useBase(ws);
  const overview = useIgOverview(ws.org.org_id);
  const ent = useEntitlements(ws.org.org_id);
  // K-375: Insights warten auf die Meta-Prüfung; bis dahin „in Vorbereitung“ statt Tarifhinweis
  const insightsBereit = useInsightsBereit();
  const [showAll, setShowAll] = useState(false);
  const { busy, error: actionError, run } = useAction();

  const rows = useMemo(() => {
    if (!base || !overview.data) return [];
    return base.talents
      .filter((t) => t.status !== "archived")
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b), "de"))
      .map((t) => {
        const account = overview.data.accounts.find((a) => a.talent_id === t.id) ?? null;
        const consent = consentInfo(overview.data.consents.find((c) => c.talent_id === t.id) ?? null);
        const active = overview.data.shares.filter((s) => s.talent_id === t.id && shareState(s, base.nowMs) === "active").length;
        return { t, account, consent, active };
      });
  }, [base, overview.data]);

  const err = error ?? overview.error;
  if (err) return <ErrorState error={err} retry={() => { retry(); void overview.refetch(); }} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base || overview.isPending || !overview.data) return <Loading />;

  const shares = overview.data.shares.map((s) => ({ s, state: shareState(s, base.nowMs), views: shareViewStats(overview.data.views, s.id) }));
  const listed = shares.filter((x) => showAll || x.state === "active" || x.state === "locked");
  // Tarifmodul für alle Rollen (org_entitlements), nicht nur für den Admin
  const moduleOff = ent.data ? (ent.data.limits.module_insights ?? 0) < 1 : false;
  // Paket 2: Zahlen für die Kacheln oben (wie im Original), gezählt aus den Zeilen dieser Seite
  const enabledCount = rows.filter((r) => r.t.ig_enabled).length;
  const connectedCount = rows.filter((r) => r.account?.status === "active").length;
  const activeShares = shares.filter((x) => x.state === "active" || x.state === "locked").length;

  return (
    <main id="main" className="content tk-insights">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>Insights</h1>
          <p>{t("Instagram-Konten, Einwilligungen der Creator und Freigaben an Marken.", "Instagram accounts, creator consents and shares with brands.")}</p>
        </div>
        <Link className="btn" to="/agentur/creator">{t("Zur Creator-Liste", "Go to creator list")}</Link>
      </div>
      {!insightsBereit ? <div className="notice" role="status">{insightsVorbereitungText()}</div>
        : moduleOff && <TarifHinweis modul="module_insights" was={t("Kennzahlen, Verbinden und Freigaben", "Metrics, connecting and shares")} />}
      {actionError && <p className="tk-error" role="alert">{actionError}</p>}

      <section className="grid three kpis tk-kacheln" aria-label={t("Kennzahlen", "Metrics")}>
        <div className="kpi">
          <small>{t("Verbinden erlaubt", "Connecting allowed")}</small>
          <div className="metric num">{t(`${enabledCount} von ${rows.length}`, `${enabledCount} of ${rows.length}`)}</div>
          <small>{t("Creator, die Instagram im Creator-Portal verbinden dürfen", "Creators who may connect Instagram in the creator portal")}</small>
        </div>
        <div className="kpi">
          <small>{t("Verbundene Konten", "Connected accounts")}</small>
          <div className="metric num">{connectedCount}</div>
          <small>{t("Instagram-Konten mit aktivem Zugang", "Instagram accounts with active access")}</small>
        </div>
        <div className="kpi">
          <small>{t("Aktive Freigaben", "Active shares")}</small>
          <div className="metric num">{activeShares}</div>
          <small>{t(`von ${shares.length} insgesamt`, `of ${shares.length} in total`)}</small>
        </div>
      </section>

      {/* Paket 2: Liste als weiße Karte mit Titel und Erklärung darüber (o-insights) */}
      <section className="panel stack tk-listenkarte" aria-labelledby="ins-konten">
        <div className="tk-listenkarte-kopf">
          <h2 id="ins-konten">{t("Verbundene Konten", "Connected accounts")}</h2>
          <p>{t("Verbinden erlaubt: Nur dann kann der Creator sein Instagram-Konto im Creator-Portal verbinden. Die Einwilligung zur Weitergabe an Marken erteilt der Creator selbst.", "Connecting allowed: only then can the creator connect their Instagram account in the creator portal. The creator gives consent to sharing with brands themselves.")}</p>
        </div>
        <div className="tablewrap tk-koop-karten tk-sticky-end">
          <table>
            <caption className="sr-only">{t("Instagram-Konten der Creator", "Creators' Instagram accounts")}</caption>
            <thead>
              <tr>
                <th scope="col">Creator</th>
                <th scope="col">Instagram</th>
                <th scope="col">Status</th>
                <th scope="col">{t("Letzter Abgleich", "Last sync")}</th>
                <th scope="col">{t("Einwilligung", "Consent")}</th>
                <th scope="col">{t("Verbinden erlaubt", "Connecting allowed")}</th>
                <th scope="col"><span className="sr-only">{t("Aktionen", "Actions")}</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ t: tl, account, consent, active }) => (
                <tr key={tl.id}>
                  <td className="tk-koop-kopf"><Link to={`/agentur/creator/${tl.id}`}><strong>{nameOf(tl)}</strong></Link></td>
                  <td data-label={t("Instagram", "Instagram")}>{account ? `@${account.username}` : tl.instagram_handle ? <><span className="muted">@{tl.instagram_handle}</span><small>{t("nicht verbunden", "not connected")}</small></> : "–"}{account && <small>{formatCount(account.followers_count)} {t("Follower", "followers")}</small>}</td>
                  <td data-label={t("Status", "Status")}><PillView pill={igStatusPill(account)} /></td>
                  <td data-label={t("Letzter Abgleich", "Last sync")}>{account?.last_sync_at ? date(account.last_sync_at, tz) : "–"}</td>
                  <td className="tk-umbruch tk-ins-einw" data-label={t("Einwilligung", "Consent")}>
                    <PillView pill={consent.allowed ? pill(t("Erteilt", "Given"), "success") : pill(t("Keine", "None"), "neutral")} />
                    {consent.allowed && <small className="tk-ins-bloecke" title={consent.blocks.map(blockLabel).join(", ")}>{consent.blocks.map(blockLabel).join(", ")}</small>}
                  </td>
                  <td className="tk-ins-schalter" data-label={t("Verbinden erlaubt", "Connecting allowed")}>
                    <button className="" aria-pressed={tl.ig_enabled} disabled={!!busy || moduleOff}
                      onClick={() => void run(`ig-${tl.id}`, () => setIgEnabled(api, tl.id, !tl.ig_enabled), tl.ig_enabled ? t(`Verbinden für ${nameOf(tl)} nicht mehr erlaubt.`, `Connecting for ${nameOf(tl)} no longer allowed.`) : t(`Verbinden für ${nameOf(tl)} erlaubt.`, `Connecting for ${nameOf(tl)} allowed.`))}>
                      {tl.ig_enabled ? t("Ja", "Yes") : t("Nein", "No")}
                    </button>
                  </td>
                  <td className="tk-koop-aktion"><Link className="btn" to={`/agentur/creator/${tl.id}/insights`}>{active ? t(`Freigeben · ${active} aktiv`, `Share · ${active} active`) : t("Freigeben", "Share")}</Link></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7}>{t("Noch keine Creator.", "No creators yet.")}</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel stack tk-listenkarte" aria-labelledby="ins-freigaben">
        <div className="row between tk-listenkarte-kopf">
          <h2 id="ins-freigaben">{t("Freigaben an Marken", "Shares with brands")}</h2>
          <div className="toolbar">
            <button className="" aria-pressed={!showAll} onClick={() => setShowAll(false)}>{t("Aktiv · ", "Active · ")}{activeShares}</button>
            <button className="" aria-pressed={showAll} onClick={() => setShowAll(true)}>{t("Alle · ", "All · ")}{shares.length}</button>
          </div>
        </div>
        <ShareTable rows={listed} base={base} tz={tz} busy={busy} showTalent onRevoke={(s) => run(`weg-${s.id}`, () => revokeShare(api, s.id), t("Freigabe zurückgezogen.", "Share withdrawn."))}
          onUnlock={(s) => run(`auf-${s.id}`, () => unlockShare(api, s.id), t("Freigabe entsperrt.", "Share unlocked."))} />
      </section>
      <DemoNote />
    </main>
  );
}
